import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Bolt, Building2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Contact Axon Muscle | Customer Support';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.contactBanner}
            alt="Contact us"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          
          {/* Cyan Glow */}
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-axon-600/15 rounded-full blur-[120px]" />
          
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-6">
                  <Bolt className="w-4 h-4" />
                  GET IN TOUCH
                </div>
                <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
                  Contact Us
                </h1>
                <p className="text-neutral-300 text-lg">
                  Have questions? We're here to help you reach your fitness goals.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Phone */}
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-start gap-4 p-6 bg-neutral-900 rounded-2xl border border-neutral-800 hover:border-axon-500/50 transition-all group"
              >
                <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-axon-600/30 transition-colors">
                  <Phone className="w-6 h-6 text-axon-500" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-1">Phone</h3>
                  <p className="text-axon-400 font-semibold">{CONTACT_INFO.phone}</p>
                  <p className="text-neutral-500 text-sm mt-1">Mon-Fri 9am-5pm EST</p>
                </div>
              </a>

              {/* Email */}
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-start gap-4 p-6 bg-neutral-900 rounded-2xl border border-neutral-800 hover:border-axon-500/50 transition-all group"
              >
                <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-axon-600/30 transition-colors">
                  <Mail className="w-6 h-6 text-axon-500" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-1">Email</h3>
                  <p className="text-axon-400 font-semibold">{CONTACT_INFO.email}</p>
                  <p className="text-neutral-500 text-sm mt-1">We'll respond within 24 hours</p>
                </div>
              </a>

              {/* Address */}
              <div className="flex items-start gap-4 p-6 bg-neutral-900 rounded-2xl border border-neutral-800">
                <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-axon-500" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-1">Address</h3>
                  <p className="text-neutral-300">
                    {CONTACT_INFO.address.street}<br />
                    {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                    {CONTACT_INFO.address.country}
                  </p>
                </div>
              </div>

              {/* Business Hours */}
              <div className="flex items-start gap-4 p-6 bg-neutral-900 rounded-2xl border border-neutral-800">
                <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-axon-500" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-white uppercase tracking-wide mb-1">Business Hours</h3>
                  <p className="text-neutral-300">Monday - Friday: 9am - 5pm EST</p>
                  <p className="text-neutral-500 text-sm mt-1">Closed weekends & holidays</p>
                </div>
              </div>

              {/* Corporate Name Card */}
              <div className="p-6 bg-gradient-to-br from-axon-600/20 to-electric-500/10 rounded-2xl border border-axon-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <Building2 className="w-6 h-6 text-axon-500" />
                  <h3 className="font-display font-bold text-white uppercase tracking-wide">Corporate Name</h3>
                </div>
                <p className="text-axon-400 font-semibold text-lg">{CONTACT_INFO.corpName}</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-neutral-900 rounded-3xl border border-neutral-800 p-8 lg:p-10">
                <h2 className="font-display text-2xl text-white uppercase tracking-tight mb-2">Send Us a Message</h2>
                <p className="text-neutral-400 mb-8">Fill out the form below and we'll get back to you as soon as possible.</p>
                
                {submitted ? (
                  <div className="flex items-center gap-4 p-6 bg-green-500/10 border border-green-500/30 rounded-2xl">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                    <div>
                      <h3 className="text-white font-semibold">Message Sent!</h3>
                      <p className="text-neutral-400 text-sm">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Your Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-5 py-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:ring-2 focus:ring-axon-500 focus:border-axon-500 transition-all"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Email Address</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-5 py-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:ring-2 focus:ring-axon-500 focus:border-axon-500 transition-all"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">Subject</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full px-5 py-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:ring-2 focus:ring-axon-500 focus:border-axon-500 transition-all"
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-300 mb-2">Message</label>
                      <textarea
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full px-5 py-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:ring-2 focus:ring-axon-500 focus:border-axon-500 transition-all resize-none"
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button hover:shadow-glow-cyan"
                    >
                      <Send className="w-5 h-5" />
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
